import React from 'react';
import { Card, Badge, Dropdown } from 'react-bootstrap';
import {
  Shirt, Edit, Trash2, MoreVertical,
  Clock, DollarSign, Images,
  ImagePlus
} from 'lucide-react';
import modelService from '../../../services/modelService';

/**
 * ✅ ATELIER: Card d'affichage d'un modèle du catalogue.
 *
 * ✅ MODIFIÉ:
 *   - Suppression du toggle "tissu" (concept retiré)
 *   - Affiche l'image principale (issue de la galerie) + badge nombre d'images
 *     si plusieurs photos sont disponibles.
 */
const ModelCard = ({
  model,
  onEdit,
  onDelete,
  onView,
  isManager
}) => {

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(model);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(model);
  };

  const handleView = () => {
    onView?.(model);
  };

  // ✅ MODIFIÉ: utilise getPrimaryImageUrl qui cherche d'abord dans la galerie,
  // puis tombe sur model.imageUrl (rétrocompatibilité)
  const displayedImageUrl = modelService.getPrimaryImageUrl(model);

  // ✅ NOUVEAU: nombre total d'images dans la galerie
  const imageCount = model.imageCount
    ?? (Array.isArray(model.images) ? model.images.length : 0);
  const hasMultipleImages = imageCount > 1;

  return (
    <Card
      className="model-card h-100 shadow-sm"
      onClick={handleView}
      style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
    >
      {/* Image principale du modèle */}
      <div className="model-image-container">
        {displayedImageUrl ? (
          <img
            src={displayedImageUrl}
            alt={model.name}
            className="model-image"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
        ) : null}

        <div
          className="model-image-placeholder"
          style={{ display: displayedImageUrl ? 'none' : 'flex' }}
        >
          <Shirt size={48} />
        </div>

        {/* Badge catégorie */}
        {model.category && (
          <Badge
            bg="light"
            text="dark"
            className="model-category-badge"
          >
            {model.category}
          </Badge>
        )}

        {/* ✅ NOUVEAU: Badge nombre d'images (uniquement si > 1) */}
        {hasMultipleImages && (
          <div className="model-images-count" title={`${imageCount} photos`}>
            <ImagePlus size={12} className="me-1" />
            {imageCount}
          </div>
        )}

        {/* Menu actions (manager uniquement) */}
        {isManager && (
          <div className="model-actions-menu">
            <Dropdown onClick={(e) => e.stopPropagation()}>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                className="model-actions-toggle"
              >
                <MoreVertical size={16} />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={handleEdit}>
                  <Edit size={14} className="me-2" />
                  Modifier
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleDelete} className="text-danger">
                  <Trash2 size={14} className="me-2" />
                  Supprimer
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>

      <Card.Body>
        <h6 className="model-name mb-2">{model.name}</h6>

        {model.description && (
          <p className="model-description text-muted small mb-3">
            {model.description.length > 80
              ? model.description.substring(0, 80) + '...'
              : model.description}
          </p>
        )}

        <div className="model-info">
          <div className="info-row">
            <DollarSign size={14} className="info-icon text-success" />
            <span className="info-label">Prix</span>
            <strong className="info-value">
              {modelService.formatPrice(model.basePrice)}
            </strong>
          </div>

          <div className="info-row">
            <Clock size={14} className="info-icon text-primary" />
            <span className="info-label">Durée</span>
            <span className="info-value">
              {modelService.formatEstimatedDays(model.estimatedDays)}
            </span>
          </div>
        </div>
      </Card.Body>

      <style>{`
        .model-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important;
        }

        .model-image-container {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
          border-top-left-radius: var(--bs-card-inner-border-radius);
          border-top-right-radius: var(--bs-card-inner-border-radius);
        }

        .model-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .model-card:hover .model-image {
          transform: scale(1.05);
        }

        .model-image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #adb5bd;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .model-category-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          font-size: 0.75rem;
          padding: 0.35em 0.65em;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        /* ✅ NOUVEAU: Badge nombre d'images */
        .model-images-count {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(0, 0, 0, 0.65);
          color: white;
          border-radius: 12px;
          padding: 3px 10px;
          font-size: 0.75rem;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          backdrop-filter: blur(4px);
          z-index: 2;
        }

        .model-actions-menu {
          position: absolute;
          top: 8px;
          right: 8px;
        }

        .model-actions-toggle {
          border-radius: 50% !important;
          width: 32px;
          height: 32px;
          padding: 0 !important;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
        }

        .model-actions-toggle::after {
          display: none !important;
        }

        .model-name {
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          line-height: 1.3;
        }

        .model-description {
          line-height: 1.4;
          margin: 0;
        }

        .model-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .info-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .info-icon {
          flex-shrink: 0;
        }

        .info-label {
          color: #6b7280;
        }

        .info-value {
          margin-left: auto;
          color: #1f2937;
        }
      `}</style>
    </Card>
  );
};

export default ModelCard;